import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { searchCities, type CityHit } from '../utils/api';
import { stateLabel, useT } from '../i18n';

// City autocomplete for the widget's suggestion form — port of the admin's
// CityAutocomplete, but querying our /api/cities proxy instead of Photon
// directly (third-party embed pages' CSP could block the direct call).
// The server already filters to Germany and whitelists the Bundesland.

export interface CitySelection {
  city_name: string;
  federal_state: string | null;
  latitude: number;
  longitude: number;
}

interface Props {
  id?: string;
  value: string;
  onChange: (city: string) => void;
  onSelect: (selection: CitySelection) => void;
  /** Fires when the lookup service errors — the form falls back to free text. */
  onLookupFailure: () => void;
  /** Reports how many suggestions the current text produced, so the form can
      require a pick only when there was something to pick. */
  onResults?: (count: number) => void;
  placeholder?: string;
}

const Wrapper = styled.div`
  position: relative;
`;

// Same input treatment as the other FeedbackDialog fields.
const Input = styled.input`
  font-family: var(--sans-stack);
  font-size: ${scaled(14)};
  color: inherit;
  padding: 8px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.surface};
  outline: none;
  width: 100%;
  box-sizing: border-box;

  &:focus { border-color: ${theme.colors.primary}; }
  &::placeholder { color: ${theme.colors.text.muted}; }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(24, 28, 68, 0.12);
`;

const Suggestion = styled.div<{ active?: boolean }>`
  padding: 8px 10px;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  cursor: pointer;
  background: ${p => (p.active ? 'rgba(24,28,68,0.08)' : 'transparent')};
  border-bottom: 1px solid ${theme.colors.border};
  &:last-child { border-bottom: none; }
`;

const SuggestionName = styled.div`
  color: ${theme.colors.text.primary};
  font-weight: ${theme.fontWeight.medium};
`;

const SuggestionMeta = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  margin-top: 2px;
`;

const StatusLine = styled.div`
  padding: 8px 10px;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
`;

export function CityAutocomplete({ id, value, onChange, onSelect, onLookupFailure, onResults, placeholder }: Props) {
  const m = useT();
  const [suggestions, setSuggestions] = useState<CityHit[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Search is triggered ONLY from the input's onChange (real user typing), not
  // from an effect watching `value`. A prefilled value (correction mode) must
  // never auto-open the suggestions — and a mount-effect guard would be
  // defeated by StrictMode's double-invoked effects in dev.
  const scheduleSearch = (raw: string) => {
    const q = raw.trim();
    if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    if (q.length < 2) {
      abortRef.current?.abort();
      setSuggestions([]);
      setOpen(false);
      setError(false);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setError(false);
      searchCities(q, ctrl.signal)
        .then(hits => {
          setSuggestions(hits);
          setOpen(true);
          setActiveIdx(-1);
          onResults?.(hits.length);
        })
        .catch(err => {
          if ((err as Error).name === 'AbortError') return;
          setSuggestions([]);
          setError(true);
          setOpen(true);
          onLookupFailure();
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false);
        });
    }, 250);
  };

  // Cleanup pending debounce/fetch on unmount.
  useEffect(() => () => {
    if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    abortRef.current?.abort();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pickSuggestion = (hit: CityHit) => {
    // Cancel any pending debounce so the pick doesn't reopen the dropdown.
    if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    onSelect({
      city_name: hit.city_name,
      federal_state: hit.federal_state,
      latitude: hit.latitude,
      longitude: hit.longitude,
    });
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      const picked = suggestions[activeIdx];
      if (picked) pickSuggestion(picked);
    } else if (e.key === 'Escape') {
      // Swallow it so the dialog's own Escape handler doesn't also close.
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Input
        id={id}
        value={value}
        maxLength={100}
        onChange={e => { onChange(e.target.value); scheduleSearch(e.target.value); }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (
        <Dropdown>
          {loading && suggestions.length === 0 && <StatusLine>{m.city.searching}</StatusLine>}
          {error && <StatusLine>{m.city.unavailable}</StatusLine>}
          {!error && suggestions.map((hit, i) => {
            const meta = [hit.federal_state && stateLabel(hit.federal_state), hit.postcode].filter(Boolean).join(' · ');
            return (
              <Suggestion
                key={`${hit.city_name}-${hit.latitude}-${i}`}
                active={i === activeIdx}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={e => { e.preventDefault(); pickSuggestion(hit); }}
              >
                <SuggestionName>{hit.city_name}</SuggestionName>
                {meta && <SuggestionMeta>{meta}</SuggestionMeta>}
              </Suggestion>
            );
          })}
          {!loading && !error && suggestions.length === 0 && value.trim().length >= 2 && (
            <StatusLine>{m.city.noHits}</StatusLine>
          )}
        </Dropdown>
      )}
    </Wrapper>
  );
}
